import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { User, LogOut, Menu, X } from 'lucide-react';

const Navbar = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_balance-wheel-app/artifacts/acwqy6kw_Life%20Worth%20Funding%20Logo.png" 
              alt="Life Worth Funding" 
              className="h-10"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                isActive('/') ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              Home
            </Link>
            <Link
              to="/assessment"
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                isActive('/assessment') ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              Assessment
            </Link>
            {user?.isPremium && (
              <Link
                to="/progress"
                className={`text-sm font-medium transition-colors hover:text-green-600 ${
                  isActive('/progress') ? 'text-green-600' : 'text-gray-700'
                }`}
              >
                Progress
              </Link>
            )}
            <Link
              to="/customize"
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                isActive('/customize') ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              Customize
            </Link>
            <Link
              to="/coaching"
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                isActive('/coaching') ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              Coaching Tools
            </Link>
            <Link
              to="/premium"
              className={`text-sm font-medium transition-colors hover:text-green-600 ${
                isActive('/premium') ? 'text-green-600' : 'text-gray-700'
              }`}
            >
              Premium
            </Link>
            {user?.isAdmin && (
              <>
                <Link
                  to="/admin-coaching-tools"
                  className={`text-sm font-medium transition-colors hover:text-purple-600 ${
                    isActive('/admin-coaching-tools') ? 'text-purple-600' : 'text-gray-700'
                  }`}
                >
                  Admin Coaching
                </Link>
                <Link
                  to="/admin-premium"
                  className={`text-sm font-medium transition-colors hover:text-purple-600 ${
                    isActive('/admin-premium') ? 'text-purple-600' : 'text-gray-700'
                  }`}
                >
                  Admin Premium
                </Link>
                <Link
                  to="/admin-users"
                  className={`text-sm font-medium transition-colors hover:text-purple-600 ${
                    isActive('/admin-users') ? 'text-purple-600' : 'text-gray-700'
                  }`}
                >
                  Admin Users
                </Link>
              </>
            )}
          </div>

          {/* Desktop User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <User className="w-4 h-4" />
                  <span>{user.name}</span>
                  {user.isPremium && (
                    <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                      Premium
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm">
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive('/') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Home
              </Link>
              <Link
                to="/assessment"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive('/assessment') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Assessment
              </Link>
              {user?.isPremium && (
                <Link
                  to="/progress"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                    isActive('/progress') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Progress
                </Link>
              )}
              <Link
                to="/customize"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive('/customize') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Customize
              </Link>
              <Link
                to="/coaching"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive('/coaching') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Coaching Tools
              </Link>
              <Link
                to="/premium"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  isActive('/premium') ? 'bg-green-50 text-green-600' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Premium
              </Link>
              
              {user?.isAdmin && (
                <>
                  <Link
                    to="/admin-coaching-tools"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                      isActive('/admin-coaching-tools') ? 'bg-purple-50 text-purple-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Admin Coaching
                  </Link>
                  <Link
                    to="/admin-premium"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                      isActive('/admin-premium') ? 'bg-purple-50 text-purple-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Admin Premium
                  </Link>
                  <Link
                    to="/admin-users"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 py-2 text-base font-medium rounded-md transition-colors ${
                      isActive('/admin-users') ? 'bg-purple-50 text-purple-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Admin Users
                  </Link>
                </>
              )}

              {/* Mobile User Section */}
              <div className="pt-4 border-t border-gray-200">
                {user ? (
                  <div className="space-y-3">
                    <div className="px-3 py-2 flex items-center space-x-2">
                      <User className="w-5 h-5 text-gray-700" />
                      <span className="text-base font-medium text-gray-700">{user.name}</span>
                      {user.isPremium && (
                        <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          Premium
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        onLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left text-base font-medium text-red-600 hover:bg-red-50 rounded-md flex items-center space-x-2"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-base font-medium text-green-600 hover:bg-green-50 rounded-md"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
